import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import type { Id } from "@/convex/src/_generated/dataModel";

/**
 * App Workspace Context
 *
 * Manages the currently active app workspace (different from MCP server workspaces)
 * Handles workspace selection, creation, and switching
 */

export interface AppWorkspace {
  _id: Id<"appWorkspaces">;
  name: string;
  ownerId: Id<"users">;
  appConfig?: {
    openaiApiKey?: string;
    openaiAssistantId?: string;
  };
  billingStatus: "trial" | "active" | "past_due" | "canceled";
  billingEmail?: string;
  primaryServerId?: Id<"appWorkspaceServers">; // Reference to primary MCP server
  createdAt: number;
  updatedAt: number;
  role: "owner" | "admin" | "member" | "viewer";
}

interface AppWorkspaceContextValue {
  // Current workspace
  activeWorkspace: AppWorkspace | null;
  activeWorkspaceId: Id<"appWorkspaces"> | null;

  // All workspaces user has access to
  workspaces: AppWorkspace[];

  // Loading states
  isLoadingWorkspaces: boolean;

  // Actions
  switchWorkspace: (workspaceId: Id<"appWorkspaces">) => void;
  createWorkspace: (
    name: string,
    billingEmail?: string,
  ) => Promise<Id<"appWorkspaces">>;
  updateWorkspace: (
    workspaceId: Id<"appWorkspaces">,
    updates: {
      name?: string;
      appConfig?: { openaiApiKey?: string; openaiAssistantId?: string };
    },
  ) => Promise<void>;
  deleteWorkspace: (workspaceId: Id<"appWorkspaces">) => Promise<void>;

  // User's role in active workspace
  userRole: "owner" | "admin" | "member" | "viewer" | null;
}

const AppWorkspaceContext = createContext<AppWorkspaceContextValue | null>(
  null,
);

const STORAGE_KEY = "app-workspace-active-id";

export function AppWorkspaceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeWorkspaceId, setActiveWorkspaceId] =
    useState<Id<"appWorkspaces"> | null>(() => {
      // Load from localStorage on mount
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored as Id<"appWorkspaces"> | null;
      }
      return null;
    });

  // Query all workspaces
  const workspacesData = useQuery(api.workspaces.list, {});
  const workspaces = workspacesData ?? [];
  const isLoadingWorkspaces = workspacesData === undefined;

  // Get active workspace details
  const activeWorkspace =
    workspaces.find((w) => w._id === activeWorkspaceId) ?? null;

  // Mutations
  const createWorkspaceMutation = useMutation(api.workspaces.create);
  const updateWorkspaceMutation = useMutation(api.workspaces.update);
  const deleteWorkspaceMutation = useMutation(api.workspaces.deleteWorkspace);

  // Auto-select first workspace if none selected
  // Also clear stale workspace IDs that the user doesn't have access to
  useEffect(() => {
    // Wait for workspaces to finish loading before making decisions
    if (isLoadingWorkspaces) return;

    if (activeWorkspaceId) {
      // Check if the stored workspace ID is actually in the user's workspaces
      const workspaceExists = workspaces.some(
        (w) => w._id === activeWorkspaceId,
      );

      if (!workspaceExists) {
        // Stale workspace ID - user doesn't have access to it
        // This can happen after signup when localStorage has a stale ID
        console.log(
          "[AppWorkspace] Clearing stale workspace ID from localStorage:",
          activeWorkspaceId,
        );

        if (workspaces.length > 0) {
          // Switch to first available workspace
          const firstWorkspace = workspaces[0];
          setActiveWorkspaceId(firstWorkspace._id);
          localStorage.setItem(STORAGE_KEY, firstWorkspace._id);
        } else {
          // No workspaces available - clear the selection
          setActiveWorkspaceId(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } else if (workspaces.length > 0) {
      // No workspace selected but workspaces exist - select first one
      const firstWorkspace = workspaces[0];
      setActiveWorkspaceId(firstWorkspace._id);
      localStorage.setItem(STORAGE_KEY, firstWorkspace._id);
    }
  }, [activeWorkspaceId, workspaces, isLoadingWorkspaces]);

  // Persist active workspace to localStorage
  const switchWorkspace = (workspaceId: Id<"appWorkspaces">) => {
    setActiveWorkspaceId(workspaceId);
    localStorage.setItem(STORAGE_KEY, workspaceId);
  };

  const createWorkspace = async (
    name: string,
    billingEmail?: string,
  ): Promise<Id<"appWorkspaces">> => {
    const workspaceId = await createWorkspaceMutation({ name, billingEmail });
    // Auto-switch to new workspace
    switchWorkspace(workspaceId);
    return workspaceId;
  };

  const updateWorkspace = async (
    workspaceId: Id<"appWorkspaces">,
    updates: {
      name?: string;
      appConfig?: { openaiApiKey?: string; openaiAssistantId?: string };
    },
  ) => {
    await updateWorkspaceMutation({
      workspaceId,
      ...updates,
    });
  };

  const deleteWorkspace = async (workspaceId: Id<"appWorkspaces">) => {
    await deleteWorkspaceMutation({ workspaceId });

    // If we just deleted the active workspace, switch to another one
    if (workspaceId === activeWorkspaceId) {
      const remaining = workspaces.filter((w) => w._id !== workspaceId);
      if (remaining.length > 0) {
        switchWorkspace(remaining[0]._id);
      } else {
        setActiveWorkspaceId(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  };

  const value: AppWorkspaceContextValue = {
    activeWorkspace,
    activeWorkspaceId,
    workspaces,
    isLoadingWorkspaces,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    userRole: activeWorkspace?.role ?? null,
  };

  return (
    <AppWorkspaceContext.Provider value={value}>
      {children}
    </AppWorkspaceContext.Provider>
  );
}

export function useAppWorkspace() {
  const context = useContext(AppWorkspaceContext);
  if (!context) {
    throw new Error("useAppWorkspace must be used within AppWorkspaceProvider");
  }
  return context;
}
